import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DEVICE_TYPE } from "../../../../models/api/deviceManagement/device";
import { SelectModule } from "@openng/optimus-ui/select";
import { FileSelectEvent, FileUploadModule } from "@openng/optimus-ui/fileupload";
import { TextareaModule } from "@openng/optimus-ui/textarea";
import { DeviceManagementService } from "../../../../services/api/device-management/device-management.service";
import { MessageService } from "@openng/optimus-ui/api";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
@Component({
  selector: "app-software-form",
  imports: [
    ButtonComponent,
    InputTextModule,
    TranslateModule,
    SelectModule,
    FileUploadModule,
    TextareaModule,
    ProgressSpinnerModule,
    ReactiveFormsModule,
  ],
  templateUrl: "./software-form.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./software-form.scss",
})
export class SoftwareForm {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);

  public type = this.config.data.software ? signal("edit") : signal("add");
  public form: FormGroup = new FormGroup({
    version: new FormControl(
      { value: this.config.data.software?.version ?? null, disabled: this.type() === "edit" },
      Validators.required
    ),
    deviceType: new FormControl(
      { value: this.config.data.software?.deviceType ?? null, disabled: this.type() === "edit" },
      Validators.required
    ),
    remark: new FormControl(this.config.data.software?.remark ?? null),
  });

  public deviceTypes = signal(Object.values(DEVICE_TYPE));
  public translate = inject(TranslateService);
  private deviceManagement = inject(DeviceManagementService);
  private message = inject(MessageService);

  public loading = signal(false);

  public file1!: File;
  public file2!: File;

  /**
   * Sets the selected file for the given file selector (file1 or file2).
   * @param fileToSelect The identifier of the file selector to set the selected file for.
   * @param event The event that triggered the function call, containing the selected file.
   */
  public onFileSelect(fileToSelect: string, event: FileSelectEvent) {
    if (event?.files?.[0]) {
      if (fileToSelect === "file1") {
        this.file1 = event.files[0];
      } else if (fileToSelect === "file2") {
        this.file2 = event.files[0];
      }
    }
  }

  /**
   * Saves the software version details.
   * If the type is "edit", updates an existing software version.
   * If the type is "add", creates a new software version.
   * If the creation or update is successful, displays a success message and closes the dialog.
   */
  public save() {
    if (this.type() === "edit") {
      if (this.form.valid) {
        const formValues = this.form.getRawValue();
        this.loading.set(true);
        this.deviceManagement.editSoftware(formValues.version, formValues.deviceType, formValues.remark).subscribe({
          next: () => {
            this.loading.set(false);
            this.message.add({
              severity: "success",
              key: "global",
              life: 4500,
              summary: this.translate.instant("common.success"),
              detail: this.translate.instant("device_management.software.edited"),
            });
            this.ref.close(true);
          },
          error: () => {
            this.loading.set(false);
          },
        });
      }
    } else if (this.type() === "add") {
      if (this.form.valid && this.file1 && this.file2) {
        const formValues = this.form.value;
        this.loading.set(true);
        this.deviceManagement
          .createSoftware(formValues.version, formValues.deviceType, this.file1, this.file2, formValues.remark)
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.message.add({
                severity: "success",
                key: "global",
                life: 4500,
                summary: this.translate.instant("common.success"),
                detail: this.translate.instant("device_management.software.created"),
              });
              this.ref.close(true);
            },
            error: () => {
              this.loading.set(false);
            },
          });
      }
    }
  }

  /**
   * Closes the dialog without returning any value.
   */
  public closeDialog() {
    this.ref.close();
  }
}
